import { loggerService, LogTag } from "@/Utils/LoggerService";

export const SITE_NAME = "Coval Flow";
export const SITE_URL = "https://covalflow.com";

/**
 * Set to true to enable /:lang/* routing (e.g. /en/, /fr/).
 * Set to false for a single-language app: standard routing is used instead.
 */
export const I18N_ROUTING = true;

/** Backend `APP_TIMEZONE` default: used when the build sets no zone. */
export const DEFAULT_APP_TIMEZONE = "Europe/Paris";

const isValidTimeZone = (value: string) => {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
};

/**
 * The zone a build was configured with (`VITE_APP_TIMEZONE`), or the default
 * when none or an invalid one is set: an invalid zone would make every
 * `Intl` call of the phase logic throw, so it is logged and replaced rather
 * than allowed to break the app.
 */
export const resolveAppTimeZone = (rawValue: string | undefined) => {
  const value = rawValue?.trim();

  if (!value) {
    return DEFAULT_APP_TIMEZONE;
  }

  if (!isValidTimeZone(value)) {
    loggerService.error(
      LogTag.APP,
      `Invalid VITE_APP_TIMEZONE "${value}", falling back to ${DEFAULT_APP_TIMEZONE}`,
    );
    return DEFAULT_APP_TIMEZONE;
  }

  return value;
};

/**
 * IANA timezone the app reads calendar days and day-window wall-clock times
 * in. It MUST equal the backend `APP_TIMEZONE`: the API resolves show dates,
 * phase windows and the postes it deletes in that zone, so the browser has to
 * use the same one whatever the viewer's own timezone.
 *
 * The backend exposes no endpoint for it, so it is set at build time through
 * `VITE_APP_TIMEZONE` (default "Europe/Paris", the backend default). A
 * deployment that changes the backend `APP_TIMEZONE` must build the frontend
 * with the same value.
 */
export const APP_TIMEZONE = resolveAppTimeZone(
  import.meta.env.VITE_APP_TIMEZONE,
);
